import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import request from '../../services/Request';
import LoadingIcon from '../items/buttons/LoadingIcon';
import BackButton from '../items/buttons/BackButton';
import Swal from 'sweetalert2';
import EditUserModal from '../user/EditUserModal';
import { userLimits } from './uitls/userLimits';
import ModernTable, { ModernPageContainer, ModernPageHeader } from './components/ModernTable';
import { Box, Chip, alpha, Stack, Typography, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';

const getAllEmployeesURL = `/users/getAllEmployees`;
const deleteUserURL = `/users/delete/`;
const forceLogoutUserURL = `/users/forceLogout/`;
const forceLogoutByAdminURL = `/users/forceLogoutByAdmin/`;
const forceLogoutAllURL = `/users/forceLogoutAll`;

export default function ViewMyUser(props) {
  const theme = useTheme();
  const user = JSON.parse(localStorage.getItem('user'));
  const dbaUser = user.dba;
  const { id, tableTitle, superAdmin } = userLimits(
    props.match.params.id,
    props.match.params.group,
    'Users'
  );

  let history = useHistory();
  const [employees, setEmployees] = useState([]);
  const [reflesh, setReflesh] = useState(false);
  const [isloading, setIsLoading] = useState(false);
  const [show, setshow] = useState(false);
  const [rowdata, setrowdata] = useState(null);
  const [pagination, setPagination] = useState(
    localStorage.getItem('table_view_user') ? localStorage.getItem('table_view_user') : 10
  );

  useEffect(() => {
    localStorage.setItem('table_view_user', pagination);
  }, [pagination]);

  useEffect(() => {
    let isCancelled = false;

    //getAllEmployees
    async function getAllEmployees() {
      await request({
        method: 'post',
        url: getAllEmployeesURL,
        data: {
          id: id,
          dba: dbaUser,
        },
      }).then((res) => {
        if (!isCancelled) {
          setEmployees(res.data);
          setIsLoading(true);
        }
      });
    }

    getAllEmployees();

    return () => {
      isCancelled = true;
    };
  }, [reflesh]);

  const deleteUser = async (userId) => {
    await request({
      method: 'delete',
      url: deleteUserURL + userId,
    }).then();
    Swal.fire({
      text: `The user has been successfully deleted`,
      icon: 'success',
    });
    reflesh ? setReflesh(false) : setReflesh(true);
  };

  const handleForceLogoutUser = (rowData) => {
    Swal.fire({
      title: 'Force Logout User?',
      text: `${rowData.firstname} ${rowData.lastname} (${rowData.email}) will be logged out within 5 minutes.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Logout',
      confirmButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await request({ method: 'post', url: forceLogoutUserURL + rowData._id });
        Swal.fire({ text: 'User has been logged out.', icon: 'success' });
      }
    });
  };

  const handleForceLogoutByAdmin = () => {
    const adminEmail = props.match.params.id;
    Swal.fire({
      title: 'Force Logout All Under Admin?',
      text: `All users belonging to "${adminEmail}" (including the admin) will be logged out within 5 minutes.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Logout All',
      confirmButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await request({ method: 'post', url: forceLogoutByAdminURL + encodeURIComponent(adminEmail) });
        Swal.fire({ text: 'Admin and all associated users have been logged out.', icon: 'success' });
      }
    });
  };

  const handleForceLogoutAll = () => {
    Swal.fire({
      title: 'Force Logout ALL Users?',
      text: 'Every user in the system will be logged out within 5 minutes. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Logout Everyone',
      confirmButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await request({ method: 'post', url: forceLogoutAllURL });
        Swal.fire({ text: 'All users have been logged out.', icon: 'success' });
      }
    });
  };

  const getInitials = (firstname, lastname) => {
    return `${firstname?.charAt(0) || ''}${lastname?.charAt(0) || ''}`.toUpperCase();
  };

  const getAvatarColor = (fullAccess) => {
    return fullAccess
      ? alpha(theme.palette.success.main, 0.2)
      : alpha(theme.palette.warning.main, 0.2);
  };

  const getAvatarTextColor = (fullAccess) => {
    return fullAccess ? theme.palette.success.dark : theme.palette.warning.dark;
  };

  const columns = [
    {
      title: 'User',
      field: 'lastname',
      render: (rowData) => (
        <Stack direction="row" spacing={1.6} alignItems="center">
          <Avatar
            sx={{
              width: 35.2,
              height: 35.2,
              backgroundColor: getAvatarColor(rowData.fullAccess),
              color: getAvatarTextColor(rowData.fullAccess),
              fontWeight: 700,
              fontSize: '0.72rem',
              border: `1.6px solid ${alpha(
                rowData.fullAccess ? theme.palette.success.main : theme.palette.warning.main,
                0.3
              )}`,
            }}
          >
            {getInitials(rowData.firstname, rowData.lastname)}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.24 }}>
              {rowData.firstname} {rowData.lastname}
            </Typography>
            {rowData.dba && (
              <Chip
                label="DBA"
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.52rem',
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.info.main, 0.15),
                  color: theme.palette.info.main,
                  border: `0.8px solid ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              />
            )}
          </Box>
        </Stack>
      ),
    },
    {
      title: 'Email',
      field: 'email',
      render: (rowData) => (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontFamily: 'monospace',
            fontSize: '0.7rem',
          }}
        >
          {rowData.email}
        </Typography>
      ),
    },
    {
      title: 'Access Level',
      field: 'fullAccess',
      render: (rowData) => (
        <Chip
          label={rowData.fullAccess ? 'Full Access' : 'Limited Access'}
          icon={
            <Box
              component="span"
              sx={{
                width: 6.4,
                height: 6.4,
                borderRadius: '50%',
                backgroundColor: rowData.fullAccess
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
                ml: 0.8,
              }}
            />
          }
          sx={{
            fontWeight: 600,
            fontSize: '0.64rem',
            borderRadius: 2,
            px: 1.2,
            height: 25.6,
            backgroundColor: rowData.fullAccess
              ? alpha(theme.palette.success.main, 0.12)
              : alpha(theme.palette.warning.main, 0.12),
            color: rowData.fullAccess ? theme.palette.success.dark : theme.palette.warning.dark,
            border: `1.2px solid ${
              rowData.fullAccess
                ? alpha(theme.palette.success.main, 0.3)
                : alpha(theme.palette.warning.main, 0.3)
            }`,
          }}
        />
      ),
      lookup: { true: 'Full Access', false: 'Limited Access' },
    },
  ];

  return (
    <ModernPageContainer>
      {!isloading && <LoadingIcon />}

      {/* Header Section with Title */}
      <ModernPageHeader
          title={tableTitle || 'User Management'}
          subtitle="Manage user accounts, permissions, and access levels"
          icon={PersonIcon}
          buttons={[
            {
              text: 'Add User',
              icon: AddIcon,
              variant: 'contained',
              onClick: () => {
                if (superAdmin) {
                  history.push(`/dashboard/signUp/${props.match.params.id}/${props.match.params._id}`);
                } else {
                  history.push(`/dashboard/signUp`);
                }
              },
            },
            ...(superAdmin && props.match.params.id ? [{
              text: 'Logout Admin Group',
              icon: LogoutIcon,
              variant: 'outlined',
              color: 'error',
              onClick: handleForceLogoutByAdmin,
            }] : []),
            ...(superAdmin ? [{
              text: 'Logout All',
              icon: LogoutIcon,
              variant: 'outlined',
              color: 'error',
              onClick: handleForceLogoutAll,
            }] : []),
          ]}
        />

        {/* Modern Table */}
        <Box sx={{ mt: 2.4 }}>
          <ModernTable
            columns={columns}
            data={employees}
            onRowsPerPageChange={setPagination}
            localization={{ body: { editRow: { deleteText: 'Are you sure to delete this user' } } }}
            editable={{
              onRowDelete: (newData) =>
                new Promise((resolve) => {
                  setTimeout(() => {
                    deleteUser(newData._id);
                    resolve();
                  }, 1000);
                }),
            }}
            actions={[
              {
                icon: 'edit',
                tooltip: 'Edit User',
                onClick: (_event, rowdata) => {
                  setshow(true);
                  setrowdata(rowdata);
                },
              },
              ...(superAdmin ? [{
                icon: () => <LogoutIcon fontSize="small" />,
                tooltip: 'Force Logout',
                onClick: (_event, rowdata) => handleForceLogoutUser(rowdata),
              }] : []),
            ]}
            options={{
              actionsColumnIndex: -1,
              rowStyle: (rowData) => {
                if (rowData.dba) {
                  return {
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                    borderLeft: `4px solid ${theme.palette.info.main}`,
                  };
                }
              },
              pageSize: parseInt(pagination),
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />

        {/* Back Button */}
        <Box sx={{ mt: 1.6 }}>
          <BackButton marginTop={0} marginRight={0} centered />
        </Box>

        {/* Edit Modal */}
        <EditUserModal
          user={user}
          show={show}
          data={rowdata}
          setReflesh={setReflesh}
          reflesh={reflesh}
          onHide={() => {
            setshow(false);
          }}
        />
      </Box>
    </ModernPageContainer>
  );
}
